// External
import React, { useState, useCallback, memo } from "react";
import _ from "lodash";
import { useLazyQuery, useMutation } from "@apollo/client";

// DS
import { Modal, Button, MediaCard, IconButton } from "@toddle-edu/ds-web";
import {
  DownloadOutlined,
  AttachmentClipOutlined,
  DeleteOutlined,
} from "@toddle-edu/ds-icons";

// Hooks
import { useDispatch, useI18n } from "Hooks";

// UIComponents
import DropzoneComponent from "UIComponents/DropzoneComponent";
import StepsList from "UIComponents/StepsList";
import FullScreenLoader from "UIComponents/FullScreenLoader";

// Module
import { downloadFromUrl } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { useAcademicRecordsModal } from "../../../../context";
import { fetchTranscriptV3BulkUploadExcelTemplateQuery } from "../../modules/BulkUploadQueries";
import { validateTranscriptV3BulkUploadFromExcelMutation } from "../../modules/BulkUploadMutations";
import {
  SHEET_TYPES,
  BULK_UPLOAD_MODAL_TYPES,
  ACCEPTED_FILE_FORMATS,
  MODAL_WIDTH,
} from "../../modules/BulkUploadConstants";

// Styles
import classes from "./BulkUploadImportModal.scss";

const TEST_ID_PREFIX = "bulk-upload-import-modal";

const getModalTitle = ({ sheetType, studentName, t }) => {
  const isRatings = sheetType === SHEET_TYPES.RATINGS;
  if (studentName) {
    return isRatings
      ? t("transcript:import_academic_records_for_name", { name: studentName })
      : t("transcript:import_basic_details_for_name", { name: studentName });
  }
  return isRatings
    ? t("transcript:import_academic_records_for_multiple")
    : t("transcript:import_basic_details_for_multiple");
};

const BulkUploadImportModal = () => {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const {
    modalInfo: { metadata = {} } = {},
    openModal,
    closeModal,
  } = useAcademicRecordsModal();

  const { sheetType, studentIds, studentName } = metadata;

  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const isImportDisabled = _.isNil(uploadedFile) || isUploading;

  const [generateTemplate] = useLazyQuery(
    fetchTranscriptV3BulkUploadExcelTemplateQuery,
    {
      fetchPolicy: "no-cache",
      onCompleted: data => {
        setShowFullScreenLoader(false);
        const url = _.get(
          data,
          "transcript.fetchTranscriptV3BulkUploadExcelTemplate.url"
        );

        const sheetTypeLabel =
          sheetType === SHEET_TYPES.RATINGS
            ? t("transcript:student_academic_records")
            : t("common:basic_details");
        if (url)
          dispatch(
            downloadFromUrl({
              attachment: {
                url,
                name: studentName
                  ? `${studentName}_${sheetTypeLabel}`
                  : `${t("common:label_student_count_plural", {
                      label: t("common:multiple"),
                    })}_${sheetTypeLabel}`,
                mimeType: ACCEPTED_FILE_FORMATS,
              },
              shouldAskForSave: true,
            })
          );
      },
      onError: () => {
        setShowFullScreenLoader(false);
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      },
    }
  );

  const [validateUpload] = useMutation(
    validateTranscriptV3BulkUploadFromExcelMutation
  );

  const handleDownloadTemplate = useCallback(
    ({ isBlank }) => {
      setShowFullScreenLoader(true);
      generateTemplate({
        variables: {
          input: {
            sheetType,
            studentIds,
            isBlank,
          },
        },
      });
    },
    [generateTemplate, sheetType, studentIds]
  );

  const handleDownloadPrefilledTemplate = useCallback(() => {
    handleDownloadTemplate({ isBlank: false });
  }, [handleDownloadTemplate]);

  const handleDownloadBlankTemplate = useCallback(() => {
    handleDownloadTemplate({ isBlank: true });
  }, [handleDownloadTemplate]);

  const handleFileUpload = useCallback(attachment => {
    if (_.isEmpty(attachment)) {
      setUploadedFile(null);
      return;
    }
    setUploadedFile(attachment);
  }, []);

  const handleDeleteFile = useCallback(() => {
    setUploadedFile(null);
  }, []);

  const handleImport = useCallback(async () => {
    if (_.isNil(uploadedFile)) return;

    setShowFullScreenLoader(true);
    try {
      const { data } = await validateUpload({
        variables: {
          input: {
            fileUrl: _.get(uploadedFile, "url"),
            sheetType,
          },
        },
      });

      setShowFullScreenLoader(false);

      const result = _.get(
        data,
        "transcript.validateTranscriptV3BulkUploadFromExcel",
        {}
      );

      if (result.isSuccess && result.transactionId) {
        openModal(BULK_UPLOAD_MODAL_TYPES.BULK_UPLOAD_REVIEW, {
          sheetType,
          studentIds,
          transactionId: result.transactionId,
          summary: result.summary,
        });
      } else {
        openModal(BULK_UPLOAD_MODAL_TYPES.BULK_UPLOAD_ERROR, {
          sheetType,
          studentIds,
          studentName,
          error: result.error,
        });
      }
    } catch {
      setShowFullScreenLoader(false);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  }, [
    uploadedFile,
    validateUpload,
    sheetType,
    studentIds,
    studentName,
    openModal,
    dispatch,
  ]);

  const modalTitle = getModalTitle({ sheetType, studentName, t });

  return (
    <>
      <Modal
        isOpen={true}
        height="min-content"
        width={MODAL_WIDTH.UPLOAD}
        onClose={closeModal}
      >
        <Modal.Header
          title={modalTitle}
          isClosable={true}
          testId={`${TEST_ID_PREFIX}-header`}
        />
        <Modal.Body>
          <StepsList>
            <StepsList.Step
              stepNumber={1}
              stepDetailsWrapperClassName={classes.stepDetailsWrapper}
            >
              <StepsList.StepHeader
                value={t("common:step_number_with_label", {
                  number: 1,
                  label: t("transcript:step_1_download_template"),
                })}
              />
              <StepsList.StepCustomComponent
                className={classes.stepCustomComponent}
              >
                <div className={classes.downloadButtons}>
                  <Button
                    variant="neutral"
                    type="outlined"
                    icon={<DownloadOutlined />}
                    size="medium"
                    onClick={handleDownloadPrefilledTemplate}
                    testId={`${TEST_ID_PREFIX}-download-prefilled`}
                  >
                    {t("transcript:download_template_with_existing_records")}
                  </Button>
                  <Button
                    variant="neutral"
                    type="outlined"
                    icon={<DownloadOutlined />}
                    size="medium"
                    onClick={handleDownloadBlankTemplate}
                    testId={`${TEST_ID_PREFIX}-download-blank`}
                  >
                    {t("transcript:download_blank_template")}
                  </Button>
                </div>
              </StepsList.StepCustomComponent>
            </StepsList.Step>

            <StepsList.Step
              stepNumber={2}
              stepDetailsWrapperClassName={classes.stepDetailsWrapper}
            >
              <StepsList.StepHeader
                value={t("common:step_number_with_label", {
                  number: 2,
                  label: t("transcript:step_2_upload_template"),
                })}
              />
              <StepsList.StepDescription
                value={t("transcript:upload_template_description")}
                className={classes.uploadDescription}
              />
              <StepsList.StepCustomComponent
                className={classes.stepCustomComponent}
              >
                <div className={classes.uploadSection}>
                  {!_.isNil(uploadedFile) ? (
                    <MediaCard
                      cardType="small"
                      name={_.get(uploadedFile, "name")}
                      mimeType={_.get(uploadedFile, "mimeType")}
                      url={_.get(uploadedFile, "url")}
                      options={
                        <IconButton
                          onClick={handleDeleteFile}
                          icon={<DeleteOutlined variant="critical" />}
                          variant="primary"
                          type="plain"
                          testId={`${TEST_ID_PREFIX}-delete-file`}
                        />
                      }
                    />
                  ) : (
                    <div className={classes.uploadButton}>
                      <DropzoneComponent
                        accept={ACCEPTED_FILE_FORMATS}
                        renderComponent={
                          <Button
                            type="outlined"
                            variant="neutral"
                            disabled={isUploading}
                            icon={<AttachmentClipOutlined />}
                            testId={`${TEST_ID_PREFIX}-attach-file`}
                          >
                            {t("common:attach_file")}
                          </Button>
                        }
                        onSingleContentUploaded={handleFileUpload}
                        updateIsUploading={setIsUploading}
                        testId={`${TEST_ID_PREFIX}-dropzone`}
                      />
                    </div>
                  )}
                </div>
              </StepsList.StepCustomComponent>
            </StepsList.Step>
          </StepsList>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.modalFooter}>
            <Button
              variant="neutral"
              type="outlined"
              size="medium"
              onClick={closeModal}
              testId={`${TEST_ID_PREFIX}-cancel`}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="progressive"
              type="fill"
              size="medium"
              disabled={isImportDisabled}
              onClick={handleImport}
              testId={`${TEST_ID_PREFIX}-import`}
            >
              {t("common:import")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {showFullScreenLoader && <FullScreenLoader />}
    </>
  );
};

export default memo(BulkUploadImportModal);
